import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import { DEFAULT_CURSOR_OVERLAY_WIDTH } from '@web/entities/chart'
import type { TCursorOverlay } from '@web/features/chart-add-cursor-overlay'
import { useDispatchedActions } from '@web/shared/hooks/use-dispatched-actions.hook'

import { EChartsViewMode } from './chart-view-mode.enum'

export interface IChartRange {
  rangeX: number[] | null
  rangeY: number[] | null
}

export interface IChartOverlayState {
  chartsViewMode: EChartsViewMode
  cursorOverlay: TCursorOverlay
  chartSelectedOverlayPointIndex: number | null
  activatedBackgroundLine: boolean
  activatedTimeSlice: boolean
  rangeAxis: IChartRange
}

const initialState: IChartOverlayState = {
  chartsViewMode: EChartsViewMode.CASCADE,
  cursorOverlay: { width: DEFAULT_CURSOR_OVERLAY_WIDTH, display: false },
  chartSelectedOverlayPointIndex: null,
  activatedBackgroundLine: false,
  activatedTimeSlice: false,
  rangeAxis: { rangeX: null, rangeY: null }
}

export const chartOverlaySlice = createSlice({
  name: 'chart-overlay',
  initialState,
  reducers: {
    setChartsViewMode(state, action: PayloadAction<EChartsViewMode>) {
      state.chartsViewMode = action.payload
    },

    onUpdateCursorOverlay(
      state,
      action: PayloadAction<Partial<TCursorOverlay>>
    ) {
      state.cursorOverlay = {
        ...state.cursorOverlay,
        ...action.payload
      }
    },

    setChartSelectedOverlayPointIndex(
      state,
      action: PayloadAction<number | null>
    ) {
      state.chartSelectedOverlayPointIndex = action.payload
    },

    setActivatedBackgroundLine(state, action: PayloadAction<boolean>) {
      state.activatedBackgroundLine = action.payload
    },

    setActivatedTimeSlice(state, action: PayloadAction<boolean>) {
      state.activatedTimeSlice = action.payload
    },

    updateChartRange(state, action: PayloadAction<Partial<IChartRange>>) {
      state.rangeAxis = {
        ...state.rangeAxis,
        ...action.payload
      }
    },

    resetChartOverlayState(state) {
      state.rangeAxis = { rangeX: null, rangeY: null }
      state.chartSelectedOverlayPointIndex = null
    }
  }
})

export const chartOverlayReducer = chartOverlaySlice.reducer
export const useChartOverlayActions = () =>
  useDispatchedActions(chartOverlaySlice.actions)
